import React, { useEffect, useState } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Icon from "@mui/material/Icon";
import Chip from "@mui/material/Chip";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import PropTypes from "prop-types";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import ComplexStatisticsCard from "examples/Cards/StatisticsCards/ComplexStatisticsCard";
import { apiFetch, redirectToLogin } from "api/Api";

// Recharts components
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

const COLORS = ["#FFBB28", "#0088FE", "#00C49F", "#FF8042"];

const renderSmallPieLabel = ({ cx, cy, midAngle, outerRadius, percent, name, value }) => {
  if (!value || percent === 0) return null;
  const RADIAN = Math.PI / 180;
  const radius = outerRadius + 12;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  return (
    <text
      x={x}
      y={y}
      fill="#334155"
      textAnchor={x > cx ? "start" : "end"}
      dominantBaseline="central"
      style={{ fontSize: "10.5px", fontWeight: "700" }}
    >
      {`${name}: ${value}`}
    </text>
  );
};

function getStatusChipColor(status) {
  const s = (status || "").toLowerCase();
  if (s === "resolved") return "success";
  if (s === "pending") return "warning";
  if (s === "in progress" || s === "inprogress") return "info";
  if (s === "rejected") return "error";
  return "default";
}

const StatCard = ({ title, value, icon, color }) => (
  <Card sx={{ p: 2 }}>
    <Box display="flex" justifyContent="space-between" alignItems="center">
      <Box>
        <Typography variant="button" color="text.secondary">
          {title}
        </Typography>
        <Typography variant="h4" fontWeight="bold">{value}</Typography>
      </Box>
      <Icon sx={{ fontSize: 40, color }}>{icon}</Icon>
    </Box>
  </Card>
);

StatCard.propTypes = {
  title: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  icon: PropTypes.string.isRequired,
  color: PropTypes.string.isRequired,
};

export default function LeaderWorkerDashboard() {
  const role = localStorage.getItem("role");
  const [statsData, setStatsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        const res = await apiFetch("leader_admin/get_dashboard_stats");
        if (res.success) {
          setStatsData(res.data);
        } else {
          const msg = (res.message || "").toLowerCase();
          if (msg.includes("invalid token") || msg.includes("token expired") || msg.includes("login again")) {
            redirectToLogin();
            return;
          }
          setError(res.message || "Failed to load dashboard data");
        }
      } catch (err) {
        console.error("Error fetching stats:", err);
        const errMsg = (err.message || "").toLowerCase();
        if (
          errMsg.includes("invalid token") ||
          errMsg.includes("token expired") ||
          errMsg.includes("session expired") ||
          errMsg.includes("jwt expired") ||
          errMsg.includes("login again")
        ) {
          redirectToLogin();
          return;
        }
        setError(err.message || "Failed to connect to server");
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <Box p={4} textAlign="center">
          <Typography variant="h6">Loading Dashboard Data...</Typography>
        </Box>
      </DashboardLayout>
    );
  }

  if (error || !statsData) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <Box p={4} textAlign="center">
          <Typography variant="h6" color="error">{error || "Failed to load dashboard"}</Typography>
        </Box>
      </DashboardLayout>
    );
  }

  const { complaints, workers, system, statusChart, recentComplaints } = statsData;

  const totalComplaints = complaints?.total || 0;
  const resolved = complaints?.resolved || 0;
  const pending = complaints?.pending || 0;
  const inProgress = complaints?.in_progress || 0;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Box p={3}>
        {/* Welcome Header Banner */}
        <Box mb={3.5}>
          <Card
            sx={{
              p: 3,
              background: "linear-gradient(135deg, #1A73E8 0%, #0D47A1 100%)",
              borderRadius: "16px",
              boxShadow: "0 8px 24px rgba(26, 115, 232, 0.25)",
            }}
          >
            <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
              <Box>
                <Typography variant="h4" sx={{ color: "#ffffff", fontWeight: 700, mb: 0.5 }}>
                  {role === "Leader" ? "Leader Dashboard / प्रमुखांचे डॅशबोर्ड" : "Worker Dashboard / कार्यकर्त्यांचे डॅशबोर्ड"}
                </Typography>
                <Typography variant="button" sx={{ color: "rgba(255, 255, 255, 0.9)", display: "block", fontSize: "0.88rem" }}>
                  Overview of assigned complaints, field activities & resolution stats
                </Typography>
              </Box>
              <Box display="flex" gap={1.5} alignItems="center">
                <Chip
                  label={`${totalComplaints > 0 ? Math.round((resolved / totalComplaints) * 100) : 0}% Resolution Rate`}
                  sx={{ backgroundColor: "rgba(255,255,255,0.25)", color: "#ffffff", fontWeight: "bold", fontSize: "0.85rem" }}
                />
              </Box>
            </Box>
          </Card>
        </Box>

        {/* Stats Summary Row (Equal Height Grid) */}
        <Grid container spacing={3} alignItems="stretch" mb={4}>
          <Grid item xs={12} sm={6} md={3} sx={{ display: "flex", flexDirection: "column" }}>
            <MDBox width="100%" height="100%">
              <ComplexStatisticsCard
                color="dark"
                icon="assignment"
                title="Total Complaints"
                count={totalComplaints}
                percentage={{
                  color: "success",
                  amount: `${totalComplaints > 0 ? Math.round((resolved / totalComplaints) * 100) : 0}%`,
                  label: "Resolved Rate",
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} sm={6} md={3} sx={{ display: "flex", flexDirection: "column" }}>
            <MDBox width="100%" height="100%">
              <ComplexStatisticsCard
                color="warning"
                icon="pending_actions"
                title="Pending"
                count={pending}
                percentage={{
                  color: "warning",
                  amount: `${totalComplaints > 0 ? Math.round((pending / totalComplaints) * 100) : 0}%`,
                  label: "Pending Action",
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} sm={6} md={3} sx={{ display: "flex", flexDirection: "column" }}>
            <MDBox width="100%" height="100%">
              <ComplexStatisticsCard
                color="info"
                icon="autorenew"
                title="In Progress"
                count={inProgress}
                percentage={{
                  color: "info",
                  amount: `${inProgress}`,
                  label: "Active Work",
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} sm={6} md={3} sx={{ display: "flex", flexDirection: "column" }}>
            <MDBox width="100%" height="100%">
              <ComplexStatisticsCard
                color="success"
                icon="check_circle"
                title="Resolved"
                count={resolved}
                percentage={{
                  color: "success",
                  amount: `${resolved}`,
                  label: "Completed Tasks",
                }}
              />
            </MDBox>
          </Grid>
        </Grid>

        {/* Role Based & Chart Row */}
        <Grid container spacing={3} mt={1}>
          <Grid item xs={12} md={6}>
            <Card sx={{ p: 3, height: "100%" }}>
              <Typography variant="h6" mb={2}>
                {role === "Leader" ? "Workers & Overview" : "Overview"}
              </Typography>
              <Grid container spacing={2}>
                {role === "Leader" && (
                  <Grid item xs={6}>
                    <StatCard
                      title="Assigned Workers"
                      value={workers?.total || 0}
                      icon="groups"
                      color="primary.main"
                    />
                  </Grid>
                )}
                <Grid item xs={role === "Leader" ? 6 : 12}>
                  <StatCard
                    title="Active Schemes"
                    value={system?.schemes || 0}
                    icon="auto_awesome"
                    color="secondary.main"
                  />
                </Grid>
              </Grid>
            </Card>
          </Grid>

          {/* Status Breakdown Chart */}
          <Grid item xs={12} md={6}>
            <Card sx={{ p: 2, height: "100%" }}>
              <Typography variant="h6" mb={1}>
                Complaints Status Distribution
              </Typography>
              <ResponsiveContainer width="100%" height={280}>
                <PieChart margin={{ top: 15, right: 30, left: 30, bottom: 15 }}>
                  <Pie
                    data={statusChart && statusChart.length > 0 ? statusChart : [{ name: "No Data", value: 1 }]}
                    cx="50%"
                    cy="45%"
                    outerRadius={68}
                    dataKey="value"
                    label={renderSmallPieLabel}
                    labelLine={{ strokeWidth: 1 }}
                  >
                    {(statusChart || []).map((entry, index) => (
                      <Cell key={`cell-ld-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: "12px", paddingTop: "8px" }} />
                </PieChart>
              </ResponsiveContainer>
            </Card>
          </Grid>
        </Grid>

        {/* Recent Complaints / Activity Table */}
        <Grid container spacing={3} mt={1}>
          <Grid item xs={12}>
            <Card sx={{ p: 2 }}>
              <Typography variant="h6" mb={2}>
                Recent Complaints Activity
              </Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Title</TableCell>
                      <TableCell>Category</TableCell>
                      <TableCell>Location</TableCell>
                      <TableCell>Priority</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Date</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {recentComplaints && recentComplaints.length > 0 ? (
                      recentComplaints.map((item) => (
                        <TableRow key={item.complaint_id}>
                          <TableCell>
                            <Typography variant="button" fontWeight="medium">
                              {item.title}
                            </Typography>
                          </TableCell>
                          <TableCell>{item.category_name || "General"}</TableCell>
                          <TableCell>{item.location || "N/A"}</TableCell>
                          <TableCell>{item.priority || "Medium"}</TableCell>
                          <TableCell>
                            <Chip
                              label={item.status || "Pending"}
                              color={getStatusChipColor(item.status)}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            {item.created_at ? new Date(item.created_at).toLocaleDateString() : "N/A"}
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          <Typography variant="button" color="text.secondary">
                            No recent complaints found.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </DashboardLayout>
  );
}
